import { MOVES_DATA, type MoveData, type StatKey } from '@/data/moves';
import { type PluginLoadout } from '@/engine/plugin-system';
import { findPluginById } from '@/data/plugins';

// ---------------------------------------------------------------------------
// Existing helpers — preserved for API routes (do not remove).
// ---------------------------------------------------------------------------

// Gen-5 simplified damage formula
export function calcDamage(
  attackerLevel: number,
  attackStat: number,
  defenseStat: number,
  movePower: number,
  typeEffectiveness: number
): number {
  if (movePower === 0) return 0;
  const base = Math.floor((((2 * attackerLevel / 5 + 2) * movePower * attackStat / defenseStat) / 50 + 2));
  const random = 0.85 + Math.random() * 0.15;
  return Math.max(1, Math.floor(base * typeEffectiveness * random));
}

export function calcMaxHp(baseHp: number, level: number): number {
  return Math.floor((2 * baseHp * level / 100) + level + 10);
}

export function calcStat(baseStat: number, level: number): number {
  return Math.floor((2 * baseStat * level / 100) + 5);
}

export function getXpForLevel(level: number): number {
  return Math.floor(Math.pow(level, 3));
}

export function getXpGain(opponentLevel: number): number {
  return Math.floor(opponentLevel * 5 + 10);
}

export function pickAiMove(moveIds: (number | null)[]): number {
  const valid = moveIds.filter(Boolean) as number[];
  if (!valid.length) return 11; // fallback: Tackle
  const moveObjects = valid
    .map(id => MOVES_DATA.find(m => m.id === id))
    .filter((m): m is MoveData => Boolean(m));
  return moveObjects.sort((a, b) => b.power - a.power)[0]?.id ?? 11;
}

// Type effectiveness chart
export const TYPE_CHART: Record<string, Record<string, number>> = {
  sales:    { sales: 1, tech: 1, finance: 2, creative: 0.5, ops: 1, hr: 1, normal: 1 },
  tech:     { sales: 1, tech: 0.5, finance: 1, creative: 2, ops: 2, hr: 2, normal: 1 },
  finance:  { sales: 0.5, tech: 1, finance: 0.5, creative: 1, ops: 2, hr: 2, normal: 1 },
  creative: { sales: 2, tech: 0.5, finance: 1, creative: 1, ops: 1, hr: 1, normal: 1 },
  ops:      { sales: 1, tech: 1, finance: 0.5, creative: 1, ops: 0.5, hr: 0.5, normal: 1 },
  hr:       { sales: 2, tech: 0.5, finance: 0.5, creative: 1, ops: 2, hr: 0.5, normal: 1 },
  normal:   { sales: 1, tech: 1, finance: 1, creative: 1, ops: 1, hr: 1, normal: 1 },
};

export function getEffectiveness(attackType: string, defType1: string, defType2?: string | null): number {
  const e1 = TYPE_CHART[attackType]?.[defType1] ?? 1;
  const e2 = defType2 ? (TYPE_CHART[attackType]?.[defType2] ?? 1) : 1;
  return e1 * e2;
}

export function calcCatchChance(
  catchRate: number,
  maxHp: number,
  currentHp: number,
  ballBonus: number = 1
): number {
  return ((3 * maxHp - 2 * currentHp) * catchRate * ballBonus) / (3 * maxHp * 255);
}

// ---------------------------------------------------------------------------
// Plugin effect bridge — local to the battle engine.
//
// `plugin-system.ts` owns the loadout shape and evolution unlocks. The battle
// engine needs a richer, in-combat view of plugin effects: stat multipliers,
// granted moves, and turn-start / on-hit hooks. We derive those here from the
// loadout + plugin catalog so we don't have to mutate the plugin-system
// surface (and risk breaking other consumers).
// ---------------------------------------------------------------------------

export type BattleStatMultipliers = {
  atk: number;
  def: number;
  spd: number;
  hp: number;
  critRate: number;   // additive bonus to crit chance (e.g. 0.05 = +5%)
  accuracy: number;   // multiplicative; 1.0 = neutral
  evasion: number;    // multiplicative; 1.0 = neutral
};

export type OnTurnStartEffectId = 'heal-5pct' | 'cleanse-status';
export type OnTakeDamageEffectId = 'reduce-10pct' | 'counter';

export type BattleEffects = {
  statMultipliers: BattleStatMultipliers;
  grantedMoveIds: string[];
  onTurnStartEffects: { effect: OnTurnStartEffectId; skillName: string }[];
  onTakeDamageEffects: OnTakeDamageEffectId[];
};

const NEUTRAL_MULTIPLIERS: BattleStatMultipliers = {
  atk: 1,
  def: 1,
  spd: 1,
  hp: 1,
  critRate: 0,
  accuracy: 1,
  evasion: 1,
};

/**
 * Pluggable mapping from plugin id → in-battle effects. Kept as a function-
 * scoped table so additions don't require touching the catalog file or
 * the public plugin-system surface. Plugins not listed contribute nothing
 * in battle (they may still drive evolutions via `plugin-system`).
 */
type BattleEffectFragment = {
  stats?: Partial<BattleStatMultipliers>;
  grantedMoveId?: string;
  onTurnStart?: { effect: OnTurnStartEffectId; skillName: string };
  onTakeDamage?: OnTakeDamageEffectId;
};

const PLUGIN_BATTLE_EFFECTS: Record<string, BattleEffectFragment> = {
  // Seed mappings — neutral by default; balance teams can tune over time.
  'growth-stone': { stats: { hp: 1.1, atk: 1.05 } },
  'debug-shard': {
    stats: { spd: 1.1 },
    onTurnStart: { effect: 'heal-5pct', skillName: 'Patch Cycle' },
  },
  'brand-crown': {
    stats: { critRate: 0.05, evasion: 1.1 },
    onTakeDamage: 'reduce-10pct',
  },
};

export function computeBattleEffects(loadout: PluginLoadout): BattleEffects {
  const stats: BattleStatMultipliers = { ...NEUTRAL_MULTIPLIERS };
  const grantedMoveIds: string[] = [];
  const onTurnStartEffects: BattleEffects['onTurnStartEffects'] = [];
  const onTakeDamageEffects: OnTakeDamageEffectId[] = [];

  for (const id of loadout.equipped) {
    // Only honour plugins that exist in the catalog; protects against
    // stale save data referencing a removed plugin id.
    if (!findPluginById(id)) continue;
    const frag = PLUGIN_BATTLE_EFFECTS[id];
    if (!frag) continue;

    if (frag.stats) {
      // Multiplicative for stat scalars; additive for critRate.
      if (typeof frag.stats.atk === 'number') stats.atk *= frag.stats.atk;
      if (typeof frag.stats.def === 'number') stats.def *= frag.stats.def;
      if (typeof frag.stats.spd === 'number') stats.spd *= frag.stats.spd;
      if (typeof frag.stats.hp === 'number') stats.hp *= frag.stats.hp;
      if (typeof frag.stats.critRate === 'number') {
        stats.critRate += frag.stats.critRate;
      }
      if (typeof frag.stats.accuracy === 'number') {
        stats.accuracy *= frag.stats.accuracy;
      }
      if (typeof frag.stats.evasion === 'number') {
        stats.evasion *= frag.stats.evasion;
      }
    }
    if (frag.grantedMoveId) grantedMoveIds.push(frag.grantedMoveId);
    if (frag.onTurnStart) onTurnStartEffects.push(frag.onTurnStart);
    if (frag.onTakeDamage) onTakeDamageEffects.push(frag.onTakeDamage);
  }

  return { statMultipliers: stats, grantedMoveIds, onTurnStartEffects, onTakeDamageEffects };
}

export function applyStatsMultipliers(
  stats: { atk: number; def: number; spd: number },
  mult: BattleStatMultipliers
): { atk: number; def: number; spd: number } {
  return {
    atk: Math.max(1, Math.floor(stats.atk * mult.atk)),
    def: Math.max(1, Math.floor(stats.def * mult.def)),
    spd: Math.max(1, Math.floor(stats.spd * mult.spd)),
  };
}

// ---------------------------------------------------------------------------
// Turn-based battle state machine (Phase 2)
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Status effects (poison / burn / paralyze / sleep)
//
// `MoveData` (data/moves.ts) carries no structured ailment field, so the
// engine owns the move → status mapping. Entries are derived thematically from
// the existing catalog (by move name) and tuned Pokémon-style: status-only
// moves apply at high reliability; damaging moves carry a low secondary chance.
// Only one status sticks at a time (no stacking).
// ---------------------------------------------------------------------------

export type StatusKind = 'none' | 'poison' | 'burn' | 'paralyze' | 'sleep';

export type StatusState = { kind: StatusKind; turns: number };

const NO_STATUS: StatusState = { kind: 'none', turns: 0 };

// ---------------------------------------------------------------------------
// Stat stages (-6..+6), Pokémon-style.
//
// This engine models a single offense stat (`atk`) and a single bulk stat
// (`def`) plus speed (`spd`). We fold the richer move taxonomy onto those:
//   atk + spa  → offense stage (affects damage dealt)
//   def + spd  → bulk stage    (affects damage taken)
//   spe        → speed stage   (affects turn order)
//   accuracy / evasion → hit-rate stages
// Stages are clamped to [-6, +6] and converted to multipliers on read.
// ---------------------------------------------------------------------------

export type StatStages = Record<StatKey, number>;

const NEUTRAL_STAGES: StatStages = {
  atk: 0, def: 0, spa: 0, spd: 0, spe: 0, accuracy: 0, evasion: 0,
};

const STAGE_MIN = -6;
const STAGE_MAX = 6;

/** Standard stage multiplier for atk/def/spe-type stats: (2+n)/2 up, 2/(2-n) down. */
function stageMultiplier(stage: number): number {
  const s = Math.max(STAGE_MIN, Math.min(STAGE_MAX, stage));
  return s >= 0 ? (2 + s) / 2 : 2 / (2 - s);
}

/** Accuracy/evasion use a 3-based table: (3+n)/3 up, 3/(3-n) down. */
function accStageMultiplier(stage: number): number {
  const s = Math.max(STAGE_MIN, Math.min(STAGE_MAX, stage));
  return s >= 0 ? (3 + s) / 3 : 3 / (3 - s);
}

/** Human-readable fragment for a stage delta, e.g. "rose sharply". */
function stageVerb(delta: number): string {
  const m = Math.abs(delta);
  if (delta > 0) return m >= 3 ? 'rose drastically' : m === 2 ? 'rose sharply' : 'rose';
  return m >= 3 ? 'fell severely' : m === 2 ? 'fell harshly' : 'fell';
}

/** Display label for a stat key in log lines. */
function statLabel(stat: StatKey): string {
  switch (stat) {
    case 'atk': return 'Attack';
    case 'def': return 'Defense';
    case 'spa': return 'Sp. Atk';
    case 'spd': return 'Sp. Def';
    case 'spe': return 'Speed';
    case 'accuracy': return 'accuracy';
    case 'evasion': return 'evasiveness';
  }
}

type StatusInflict = { status: Exclude<StatusKind, 'none'>; chance: number };

/**
 * Move name → status it can inflict, with application chance (0..1).
 * Status-only moves (power 0) apply reliably; damaging moves get a low
 * secondary-effect chance, matching genre conventions.
 */
const MOVE_STATUS: Record<string, StatusInflict> = {
  // Damaging moves with a thematic secondary ailment.
  'Burn Rate': { status: 'burn', chance: 0.3 },      // "Bleeds opponent over time"
  'Memory Leak': { status: 'poison', chance: 0.3 },  // "Gradually drains opponent's stamina"
  'Pitch Slam': { status: 'paralyze', chance: 0.3 }, // "stuns opponents"
  'Market Crash': { status: 'poison', chance: 0.3 }, // lingering downturn
  // Status-only moves repurposed to inflict an ailment reliably.
  'Vibe Check': { status: 'sleep', chance: 0.85 },   // lull the opponent
  'Growl': { status: 'paralyze', chance: 0.7 },      // unnerving
};

export type Phase =
  | 'idle'
  | 'player_turn'
  | 'move_selection'
  | 'resolving'
  | 'animating'
  | 'opponent_turn'
  | 'victory'
  | 'defeat'
  | 'caught'
  | 'fled';

export type BattleParticipant = {
  speciesId: string;
  level: number;
  hpCurrent: number;
  hpMax: number;
  moves: string[];              // move ids (string form)
  pp: Record<string, number>;
  stats: { atk: number; def: number; spd: number };
  types: string[];
  fainted?: boolean;
  /** Current status condition. Engine-managed; callers may omit (defaults to
   *  "none"). Only one status at a time, Pokémon-style. */
  status?: StatusState;
  /** Stat stages (-6..+6) applied as multipliers in combat. Engine-managed;
   *  callers may omit (defaults to all-neutral). Reset on switch-in. */
  statStages?: StatStages;
  /** Optional plugin loadout. When present, the engine applies plugin-derived
   *  effects (stat multipliers, granted moves, turn-start hooks, damage
   *  reduction/counters) during battle. Backward-compatible: callers may omit. */
  loadout?: PluginLoadout;
};

export type BattleEvent =
  | { kind: 'log'; text: string }
  | { kind: 'damage'; target: 'player' | 'opponent'; amount: number; effectiveness?: number }
  | { kind: 'phase'; phase: Phase }
  | { kind: 'status'; target: 'player' | 'opponent'; status: StatusKind; turns: number }
  | { kind: 'faint'; target: 'player' | 'opponent' }
  | { kind: 'caught' }
  | { kind: 'fled' }
  | { kind: 'end'; outcome: 'victory' | 'defeat' | 'caught' | 'fled' };

type Side = 'player' | 'opponent';

type Outcome = 'victory' | 'defeat' | 'caught' | 'fled';

interface BattleEngineOptions {
  isWild?: boolean;
  onEvent?: (e: BattleEvent) => void;
}

/**
 * Locates a move definition by its id (accepts numeric or string form).
 * Falls back to a normal-type 40-power "Tackle"-like move if the id isn't
 * registered (defensive — UI should never select an unknown id).
 */
function lookupMove(moveId: string): MoveData {
  const numericId = Number.parseInt(moveId, 10);
  const found = Number.isFinite(numericId)
    ? MOVES_DATA.find(m => m.id === numericId)
    : undefined;
  if (found) return found;
  return {
    id: -1,
    name: moveId,
    type: 'normal',
    power: 40,
    accuracy: 100,
    category: 'physical',
    pp: 1,
    description: 'Unknown move',
  };
}

export class BattleEngine {
  private _phase: Phase = 'idle';
  private _player: BattleParticipant;
  private _opponent: BattleParticipant;
  private readonly isWild: boolean;
  private readonly onEvent: (e: BattleEvent) => void;
  private ended = false;

  // Cached plugin-derived state. participant.stats stays untouched on the
  // public surface; damage calc uses these multiplied views instead.
  private _playerEffects: BattleEffects | null = null;
  private _opponentEffects: BattleEffects | null = null;
  private _playerEffectiveStats: { atk: number; def: number; spd: number };
  private _opponentEffectiveStats: { atk: number; def: number; spd: number };
  // Turn-start hooks run once per side per turn; tracked here so we don't
  // double-fire when both selectMove() and item/flee paths bump the phase.
  private _playerTurnStartFired = false;
  private _opponentTurnStartFired = false;

  constructor(
    player: BattleParticipant,
    opponent: BattleParticipant,
    opts: BattleEngineOptions = {}
  ) {
    this._player = { ...player, pp: { ...player.pp }, status: { ...(player.status ?? NO_STATUS) }, statStages: { ...NEUTRAL_STAGES, ...(player.statStages ?? {}) } };
    this._opponent = { ...opponent, pp: { ...opponent.pp }, status: { ...(opponent.status ?? NO_STATUS) }, statStages: { ...NEUTRAL_STAGES, ...(opponent.statStages ?? {}) } };
    this.isWild = opts.isWild ?? true;
    this.onEvent = opts.onEvent ?? (() => {});

    // Compute plugin effects (if any) and derive effective stats for damage.
    this._playerEffects = this._player.loadout
      ? computeBattleEffects(this._player.loadout)
      : null;
    this._opponentEffects = this._opponent.loadout
      ? computeBattleEffects(this._opponent.loadout)
      : null;

    this._playerEffectiveStats = this._playerEffects
      ? applyStatsMultipliers(this._player.stats, this._playerEffects.statMultipliers)
      : { ...this._player.stats };
    this._opponentEffectiveStats = this._opponentEffects
      ? applyStatsMultipliers(this._opponent.stats, this._opponentEffects.statMultipliers)
      : { ...this._opponent.stats };

    // Apply hpMax multiplier from loadout (if any). Keep hpCurrent within bounds.
    if (this._playerEffects && this._playerEffects.statMultipliers.hp !== 1) {
      const scaledMax = Math.max(1, Math.floor(this._player.hpMax * this._playerEffects.statMultipliers.hp));
      this._player.hpMax = scaledMax;
      this._player.hpCurrent = Math.min(this._player.hpCurrent, scaledMax);
    }
    if (this._opponentEffects && this._opponentEffects.statMultipliers.hp !== 1) {
      const scaledMax = Math.max(1, Math.floor(this._opponent.hpMax * this._opponentEffects.statMultipliers.hp));
      this._opponent.hpMax = scaledMax;
      this._opponent.hpCurrent = Math.min(this._opponent.hpCurrent, scaledMax);
    }

    // Expand move lists: tool-plugin-granted moves get appended (no dupes).
    // PP defaults to the move's catalog pp.
    if (this._playerEffects) {
      this.appendGrantedMoves(this._player, this._playerEffects.grantedMoveIds);
    }
    if (this._opponentEffects) {
      this.appendGrantedMoves(this._opponent, this._opponentEffects.grantedMoveIds);
    }

    this.setPhase('player_turn');
    // Fire the very first player turn-start hook (e.g. heal-5pct passive).
    this.runTurnStart('player');
  }

  private appendGrantedMoves(participant: BattleParticipant, moveIds: string[]): void {
    for (const id of moveIds) {
      if (participant.moves.includes(id)) continue;
      participant.moves.push(id);
      if (typeof participant.pp[id] !== 'number') {
        const def = lookupMove(id);
        participant.pp[id] = def.pp;
      }
    }
  }

  get phase(): Phase {
    return this._phase;
  }

  get player(): Readonly<BattleParticipant> {
    return this._player;
  }

  get opponent(): Readonly<BattleParticipant> {
    return this._opponent;
  }

  // -------------------------------------------------------------------------
  // Public actions
  // -------------------------------------------------------------------------

  async selectMove(moveId: string): Promise<void> {
    if (this.ended) return;
    if (this._phase !== 'player_turn' && this._phase !== 'move_selection') {
      this.log(`Cannot select a move during phase: ${this._phase}`);
      return;
    }
    if (!this._player.moves.includes(moveId)) {
      this.log('That move is not known.');
      return;
    }
    if ((this._player.pp[moveId] ?? 0) <= 0) {
      this.log('No PP left for that move.');
      return;
    }

    this.setPhase('resolving');

    // Opponent's turn-start hook fires once per round before resolution.
    this.runTurnStart('opponent');

    const playerMove = moveId;
    const opponentMove = this.pickOpponentMove();

    // Turn order: priority bracket first, then speed, then a coin flip.
    const playerPrio = lookupMove(playerMove).priority ?? 0;
    const opponentPrio = opponentMove !== null ? (lookupMove(opponentMove).priority ?? 0) : -Infinity;
    const playerSpd = this.speedOf('player');
    const opponentSpd = this.speedOf('opponent');
    const playerFirst =
      playerPrio > opponentPrio ||
      (playerPrio === opponentPrio &&
        (playerSpd > opponentSpd ||
          (playerSpd === opponentSpd && Math.random() < 0.5)));

    if (playerFirst) {
      this.tryAttack('player', playerMove);
      if (this.checkEnd()) return;
      if (opponentMove !== null) {
        this.tryAttack('opponent', opponentMove);
      } else {
        this.log(`${this.nameOf('opponent')} has no usable moves!`);
      }
    } else {
      if (opponentMove !== null) {
        this.tryAttack('opponent', opponentMove);
      } else {
        this.log(`${this.nameOf('opponent')} has no usable moves!`);
      }
      if (this.checkEnd()) return;
      this.tryAttack('player', playerMove);
    }

    if (this.checkEnd()) return;

    // End-of-turn residual damage (poison / burn). Order: player then opponent.
    this.tickStatus('player');
    if (this.checkEnd()) return;
    this.tickStatus('opponent');
    if (this.checkEnd()) return;

    this.setPhase('player_turn');
    // Reset turn-start flags so they fire again next round.
    this._playerTurnStartFired = false;
    this._opponentTurnStartFired = false;
    this.runTurnStart('player');
  }

  async useItem(itemId: string): Promise<void> {
    if (this.ended) return;
    if (this._phase !== 'player_turn' && this._phase !== 'move_selection') {
      this.log(`Cannot use items during phase: ${this._phase}`);
      return;
    }

    this.setPhase('resolving');

    const id = itemId.toLowerCase();
    if (id.includes('ball') || id.includes('catch')) {
      if (!this.isWild) {
        this.log("You can't catch a trainer's Surggie!");
        this.setPhase('player_turn');
        return;
      }
      const ratio = this._opponent.hpMax > 0
        ? this._opponent.hpCurrent / this._opponent.hpMax
        : 1;
      const chance = (1 - ratio) * 0.5 + 0.2;
      this.log(`You threw a ${itemId}!`);
      if (Math.random() < chance) {
        this.emit({ kind: 'caught' });
        this.endBattle('caught');
        return;
      }
      this.log('It broke free!');
    } else if (id.includes('potion') || id.includes('heal')) {
      const healAmount = Math.min(
        20,
        this._player.hpMax - this._player.hpCurrent
      );
      this._player.hpCurrent += healAmount;
      this.log(`You used a ${itemId}. Restored ${healAmount} HP.`);
    } else {
      this.log(`Used ${itemId}, but nothing happened.`);
    }

    // Opponent still gets a turn after item use.
    const opponentMove = this.pickOpponentMove();
    if (opponentMove !== null) {
      this.executeAttack('opponent', opponentMove);
    }
    if (this.checkEnd()) return;
    this.setPhase('player_turn');
  }

  switchSurggie(replacement: BattleParticipant): void {
    if (this.ended) return;
    this._player = { ...replacement, pp: { ...replacement.pp }, status: { ...(replacement.status ?? NO_STATUS) }, statStages: { ...NEUTRAL_STAGES } };
    this.log(`Go, ${replacement.speciesId}!`);
    this.setPhase('player_turn');
  }

  async attemptFlee(): Promise<boolean> {
    if (this.ended) return false;
    if (!this.isWild) {
      this.log("Can't flee from a trainer battle!");
      return false;
    }
    this.setPhase('resolving');

    const playerSpd = this.speedOf('player');
    const opponentSpd = this.speedOf('opponent');
    let chance: number;
    if (playerSpd > opponentSpd) {
      chance = 1;
    } else {
      chance = (playerSpd * 32) / (opponentSpd / 4) / 256 + 0.3;
    }

    if (Math.random() < chance) {
      this.log('Got away safely!');
      this.emit({ kind: 'fled' });
      this.endBattle('fled');
      return true;
    }

    this.log("Couldn't escape!");
    const opponentMove = this.pickOpponentMove();
    if (opponentMove !== null) {
      this.executeAttack('opponent', opponentMove);
    }
    if (this.checkEnd()) return false;
    this.setPhase('player_turn');
    return false;
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private executeAttack(attacker: Side, moveId: string): void {
    const atkSide =
      attacker === 'player' ? this._player : this._opponent;
    const defSide =
      attacker === 'player' ? this._opponent : this._player;
    const target: Side = attacker === 'player' ? 'opponent' : 'player';
    const atkEffects =
      attacker === 'player' ? this._playerEffects : this._opponentEffects;
    const defEffects =
      attacker === 'player' ? this._opponentEffects : this._playerEffects;

    if (atkSide.fainted || atkSide.hpCurrent <= 0) return;

    const move = lookupMove(moveId);

    // Decrement PP (clamp to 0).
    const remaining = atkSide.pp[moveId];
    if (typeof remaining === 'number') {
      atkSide.pp[moveId] = Math.max(0, remaining - 1);
    }

    this.log(`${this.nameOf(attacker)} used ${move.name}!`);
    this.setPhase('animating');

    // Accuracy check — hit% = move.accuracy * accuracyStage * pluginAcc / (evasionStage * pluginEva).
    const atkStages = atkSide.statStages ?? NEUTRAL_STAGES;
    const defStages = defSide.statStages ?? NEUTRAL_STAGES;
    const attackerAcc =
      (atkEffects ? atkEffects.statMultipliers.accuracy : 1) * accStageMultiplier(atkStages.accuracy);
    const defenderEva =
      (defEffects ? defEffects.statMultipliers.evasion : 1) * accStageMultiplier(defStages.evasion);
    const effectiveAccuracy = (move.accuracy * attackerAcc) / Math.max(0.01, defenderEva);
    if (Math.random() * 100 > effectiveAccuracy) {
      this.log(`${this.nameOf(attacker)}'s attack missed!`);
      return;
    }

    // Status moves deal no damage; their job is to inflict a condition and/or
    // apply data-driven stat-stage / heal effects.
    if (move.category === 'status' || move.power === 0) {
      const inflicted = this.tryInflictStatus(target, move);
      const applied = this.applyMoveEffects(attacker, move);
      if (!inflicted && !applied) this.log(`${move.name} had no direct effect.`);
      return;
    }

    const effectiveness = getEffectiveness(
      move.type,
      defSide.types[0] ?? 'normal',
      defSide.types[1]
    );

    const lvl = atkSide.level;
    const power = move.power;
    // Burn halves the physical attack stat while the condition lasts.
    const burned = atkSide.status?.kind === 'burn' && move.category === 'physical';
    const critBonus = atkEffects ? atkEffects.statMultipliers.critRate : 0;

    // Multi-hit: roll the number of strikes once; 2-hit moves are fixed,
    // 2-5 moves use the genre weighting (3 and 2 hits most common).
    const hitCount = move.multiHit
      ? this.rollHitCount(move.multiHit)
      : 1;

    let totalDamage = 0;
    let anyCrit = false;
    let hitsLanded = 0;

    for (let h = 0; h < hitCount; h++) {
      if (defSide.fainted || defSide.hpCurrent <= 0) break;

      const baseRand = 0.85 + Math.random() * 0.15;
      const isCrit = Math.random() < (1 / 16) + critBonus;
      const critMult = isCrit ? 2 : 1;

      // Crits ignore the attacker's offensive debuffs and defender's buffs,
      // but the simpler model here just reads stage-modified stats each hit.
      const rawAtk = this.attackOf(attacker, move.category);
      const atk = burned ? Math.max(1, Math.floor(rawAtk / 2)) : rawAtk;
      const def = this.defenseOf(target, move.category);

      const raw = ((((2 * lvl) / 5 + 2) * power * atk) / def) / 50 + 2;

      let damage = Math.floor(raw * effectiveness * critMult * baseRand);
      if (effectiveness === 0) {
        damage = 0;
      } else if (damage < 1) {
        damage = 1;
      }

      // onTakeDamage: reduce-10pct — apply BEFORE landing the hit.
      if (defEffects && defEffects.onTakeDamageEffects.includes('reduce-10pct') && damage > 0) {
        damage = Math.max(1, Math.floor(damage * 0.9));
      }

      defSide.hpCurrent = Math.max(0, defSide.hpCurrent - damage);
      this.emit({ kind: 'damage', target, amount: damage, effectiveness });

      totalDamage += damage;
      if (isCrit && damage > 0) anyCrit = true;
      hitsLanded++;

      if (defSide.hpCurrent <= 0) break;
    }

    const originalDamage = totalDamage;

    if (anyCrit) this.log('A critical hit!');
    if (hitCount > 1 && hitsLanded > 0) {
      this.log(`Hit ${hitsLanded} time${hitsLanded === 1 ? '' : 's'}!`);
    }
    if (effectiveness === 0) {
      this.log("It had no effect...");
    } else if (effectiveness > 1) {
      this.log("It's super effective!");
    } else if (effectiveness < 1) {
      this.log("It's not very effective...");
    }

    if (defSide.hpCurrent <= 0) {
      defSide.fainted = true;
      this.emit({ kind: 'faint', target });
      this.log(`${this.nameOf(target)} fainted!`);
      return;
    }

    // Secondary effects on a survivor: status ailment + data-driven stat stages.
    this.tryInflictStatus(target, move);
    this.applyMoveEffects(attacker, move);

    // onTakeDamage: counter — single retaliation (no infinite loop) at 25%
    // of the damage just absorbed. Skipped if defender just fainted.
    if (
      defEffects &&
      defEffects.onTakeDamageEffects.includes('counter') &&
      originalDamage > 0 &&
      !atkSide.fainted &&
      atkSide.hpCurrent > 0
    ) {
      const counterDmg = Math.max(1, Math.floor(originalDamage * 0.25));
      atkSide.hpCurrent = Math.max(0, atkSide.hpCurrent - counterDmg);
      this.log(`${this.nameOf(target)} struck back!`);
      this.emit({ kind: 'damage', target: attacker, amount: counterDmg });
      if (atkSide.hpCurrent <= 0) {
        atkSide.fainted = true;
        this.emit({ kind: 'faint', target: attacker });
        this.log(`${this.nameOf(attacker)} fainted!`);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Status conditions (poison / burn / paralyze / sleep)
  // -------------------------------------------------------------------------

  /** Paralyze cuts effective speed in half for turn-order purposes. */
  private speedOf(side: Side): number {
    const baseStat = side === 'player'
      ? this._playerEffectiveStats.spd
      : this._opponentEffectiveStats.spd;
    const p = side === 'player' ? this._player : this._opponent;
    const stages = p.statStages ?? NEUTRAL_STAGES;
    let base = Math.max(1, Math.floor(baseStat * stageMultiplier(stages.spe)));
    if (p.status?.kind === 'paralyze') base = Math.max(1, Math.floor(base / 2));
    return base;
  }

  /** Offense stat with atk/spa stage applied. `category` selects which stage. */
  private attackOf(side: Side, category: MoveData['category']): number {
    const baseStat = side === 'player'
      ? this._playerEffectiveStats.atk
      : this._opponentEffectiveStats.atk;
    const stages = (side === 'player' ? this._player : this._opponent).statStages ?? NEUTRAL_STAGES;
    const stage = category === 'special' ? stages.spa : stages.atk;
    return Math.max(1, Math.floor(baseStat * stageMultiplier(stage)));
  }

  /** Bulk stat with def/spd stage applied. `category` selects which stage. */
  private defenseOf(side: Side, category: MoveData['category']): number {
    const baseStat = side === 'player'
      ? this._playerEffectiveStats.def
      : this._opponentEffectiveStats.def;
    const stages = (side === 'player' ? this._player : this._opponent).statStages ?? NEUTRAL_STAGES;
    const stage = category === 'special' ? stages.spd : stages.def;
    return Math.max(1, Math.floor(baseStat * stageMultiplier(stage)));
  }

  /**
   * Pre-action gate. Resolves sleep/paralyze before the actor attacks:
   *  - sleep: skip the turn; decrement remaining turns; wake (clear) at 0.
   *  - paralyze: ~25% chance to be fully paralyzed and skip the turn.
   * Returns false (and runs no attack) when the actor cannot move.
   */
  private tryAttack(attacker: Side, moveId: string): void {
    const p = attacker === 'player' ? this._player : this._opponent;
    const status = p.status ?? NO_STATUS;

    if (status.kind === 'sleep') {
      if (status.turns > 0) {
        p.status = { kind: 'sleep', turns: status.turns - 1 };
      }
      if ((p.status?.turns ?? 0) <= 0) {
        this.clearStatus(attacker, `${this.nameOf(attacker)} woke up!`);
        // Wakes this turn but still loses the action (genre-standard).
        return;
      }
      this.emit({ kind: 'status', target: attacker, status: 'sleep', turns: p.status!.turns });
      this.log(`${this.nameOf(attacker)} is fast asleep!`);
      return;
    }

    if (status.kind === 'paralyze' && Math.random() < 0.25) {
      this.log(`${this.nameOf(attacker)} is paralyzed! It can't move!`);
      return;
    }

    this.executeAttack(attacker, moveId);
  }

  /**
   * Attempts to apply the move's mapped status to `target`. Honors the move's
   * application chance and the no-stacking rule (a target already afflicted is
   * not re-statused). Returns true if a new status was applied.
   */
  private tryInflictStatus(target: Side, move: MoveData): boolean {
    const inflict = MOVE_STATUS[move.name];
    if (!inflict) return false;

    const t = target === 'player' ? this._player : this._opponent;
    if (t.fainted || t.hpCurrent <= 0) return false;
    if ((t.status?.kind ?? 'none') !== 'none') return false; // one status at a time
    if (Math.random() > inflict.chance) return false;

    const turns = inflict.status === 'sleep'
      ? 1 + Math.floor(Math.random() * 3) // 1-3 turns of sleep
      : 0;                                // poison/burn/paralyze persist until cured
    t.status = { kind: inflict.status, turns };
    this.emit({ kind: 'status', target, status: inflict.status, turns });
    this.log(this.statusOnsetLine(target, inflict.status));
    return true;
  }

  /**
   * Rolls how many times a multi-hit move connects. Fixed ranges (e.g. [2,2])
   * always hit that count. 2-5 style ranges use genre weighting where 2 and 3
   * hits are far more common than 4 or 5.
   */
  private rollHitCount(range: [number, number]): number {
    const [min, max] = range;
    if (min >= max) return Math.max(1, min);
    if (min === 2 && max === 5) {
      // 2:3/8, 3:3/8, 4:1/8, 5:1/8 — the classic distribution.
      const r = Math.random();
      if (r < 0.375) return 2;
      if (r < 0.75) return 3;
      if (r < 0.875) return 4;
      return 5;
    }
    // Uniform fallback for arbitrary ranges (e.g. [2,3]).
    return min + Math.floor(Math.random() * (max - min + 1));
  }

  /**
   * Applies a move's data-driven `effects` (stat stages + heals). Stat targets
   * resolve relative to the attacker. Returns true if anything happened (so
   * status-only moves can avoid the "no effect" line). Stage moves at the cap
   * still count as "applied" only when they actually move the stat.
   */
  private applyMoveEffects(attacker: Side, move: MoveData): boolean {
    if (!move.effects || move.effects.length === 0) return false;
    let did = false;
    for (const eff of move.effects) {
      if (eff.kind === 'stat') {
        const side: Side =
          eff.target === 'self'
            ? attacker
            : (attacker === 'player' ? 'opponent' : 'player');
        if (this.bumpStage(side, eff.stat, eff.stages)) did = true;
      } else if (eff.kind === 'heal') {
        if (this.healSide(attacker, eff.fraction, move.name)) did = true;
      }
    }
    return did;
  }

  /**
   * Adjusts a stat stage by `delta`, clamped to [-6, +6], and emits a readable
   * log line ("X's Attack rose!"). Returns true if the stage actually changed;
   * a stat already pinned at the cap logs the genre "won't go higher/lower"
   * line and returns false.
   */
  private bumpStage(side: Side, stat: StatKey, delta: number): boolean {
    const p = side === 'player' ? this._player : this._opponent;
    if (p.fainted || p.hpCurrent <= 0) return false;
    const stages = p.statStages ?? { ...NEUTRAL_STAGES };
    p.statStages = stages;
    const before = stages[stat];
    const after = Math.max(STAGE_MIN, Math.min(STAGE_MAX, before + delta));
    const name = this.nameOf(side);
    const label = statLabel(stat);
    if (after === before) {
      this.log(delta > 0
        ? `${name}'s ${label} won't go any higher!`
        : `${name}'s ${label} won't go any lower!`);
      return false;
    }
    stages[stat] = after;
    this.log(`${name}'s ${label} ${stageVerb(after - before)}!`);
    return true;
  }

  /**
   * Heals a side by `fraction` of its max HP. Returns true if any HP was
   * restored. A full-HP target logs the "already full" line and returns false.
   */
  private healSide(side: Side, fraction: number, moveName: string): boolean {
    const p = side === 'player' ? this._player : this._opponent;
    if (p.fainted || p.hpCurrent <= 0) return false;
    if (p.hpCurrent >= p.hpMax) {
      this.log(`${this.nameOf(side)}'s HP is already full!`);
      return false;
    }
    const heal = Math.max(1, Math.floor(p.hpMax * fraction));
    const before = p.hpCurrent;
    p.hpCurrent = Math.min(p.hpMax, p.hpCurrent + heal);
    const restored = p.hpCurrent - before;
    this.log(`${this.nameOf(side)} used ${moveName} and restored ${restored} HP!`);
    this.emit({ kind: 'damage', target: side, amount: -restored });
    return restored > 0;
  }

  private statusOnsetLine(target: Side, status: Exclude<StatusKind, 'none'>): string {
    const name = this.nameOf(target);
    switch (status) {
      case 'poison':   return `${name} was poisoned!`;
      case 'burn':     return `${name} was burned!`;
      case 'paralyze': return `${name} was paralyzed! It may be unable to move!`;
      case 'sleep':    return `${name} fell asleep!`;
    }
  }

  /**
   * End-of-turn residual damage for poison and burn (~1/8 max HP). Emits the
   * existing `damage` + `faint` events so the bridge mirrors HP normally. A
   * cured/fainted target clears to "none".
   */
  private tickStatus(side: Side): void {
    const p = side === 'player' ? this._player : this._opponent;
    const kind = p.status?.kind ?? 'none';
    if (p.fainted || p.hpCurrent <= 0) {
      if (kind !== 'none') this.clearStatus(side);
      return;
    }
    if (kind !== 'poison' && kind !== 'burn') return;

    const dot = Math.max(1, Math.floor(p.hpMax / 8));
    p.hpCurrent = Math.max(0, p.hpCurrent - dot);
    this.log(kind === 'poison'
      ? `${this.nameOf(side)} is hurt by poison!`
      : `${this.nameOf(side)} is hurt by its burn!`);
    this.emit({ kind: 'damage', target: side, amount: dot });

    if (p.hpCurrent <= 0) {
      p.fainted = true;
      this.clearStatus(side);
      this.emit({ kind: 'faint', target: side });
      this.log(`${this.nameOf(side)} fainted!`);
    }
  }

  /** Clears a side's status back to "none", emitting the cleared event. */
  private clearStatus(side: Side, message?: string): void {
    const p = side === 'player' ? this._player : this._opponent;
    if ((p.status?.kind ?? 'none') === 'none') return;
    p.status = { ...NO_STATUS };
    this.emit({ kind: 'status', target: side, status: 'none', turns: 0 });
    if (message) this.log(message);
  }

  /**
   * Fires onTurnStart effects for the given side, once per round.
   * Guards prevent double-firing within the same round.
   */
  private runTurnStart(side: Side): void {
    if (side === 'player') {
      if (this._playerTurnStartFired) return;
      this._playerTurnStartFired = true;
    } else {
      if (this._opponentTurnStartFired) return;
      this._opponentTurnStartFired = true;
    }
    const effects = side === 'player' ? this._playerEffects : this._opponentEffects;
    if (!effects) return;
    const p = side === 'player' ? this._player : this._opponent;
    if (p.fainted || p.hpCurrent <= 0) return;

    for (const hook of effects.onTurnStartEffects) {
      if (hook.effect === 'heal-5pct') {
        const heal = Math.floor(p.hpMax * 0.05);
        if (heal > 0 && p.hpCurrent < p.hpMax) {
          p.hpCurrent = Math.min(p.hpMax, p.hpCurrent + heal);
          this.log(`${this.nameOf(side)}'s ${hook.skillName} restored a little HP!`);
        }
      } else if (hook.effect === 'cleanse-status') {
        if ((p.status?.kind ?? 'none') !== 'none') {
          this.clearStatus(side, `${this.nameOf(side)}'s ${hook.skillName} cleared its status!`);
        }
      }
    }
  }

  /**
   * Opponent move selection with light strategy:
   *  - Damaging moves are scored by power × type-effectiveness vs the player's
   *    types, so super-effective hits are preferred and no-effect moves avoided.
   *  - When the player has no status and the opponent holds a status move that
   *    inflicts an ailment, it leads with that unless it already has a clearly
   *    super-effective attack available.
   * Deterministic-ish: best score wins, with a small tiebreak shuffle.
   */
  private pickOpponentMove(): string | null {
    const usable = this._opponent.moves.filter(
      id => (this._opponent.pp[id] ?? 0) > 0
    );
    if (usable.length === 0) return null;

    const playerTypes = this._player.types;
    const scored = usable.map(id => {
      const move = lookupMove(id);
      const eff = getEffectiveness(move.type, playerTypes[0] ?? 'normal', playerTypes[1]);
      const isStatusMove = move.category === 'status' || move.power === 0;
      // Damage potential; status moves contribute no direct damage score.
      const damageScore = isStatusMove ? 0 : move.power * eff;
      return { id, move, eff, isStatusMove, damageScore };
    });

    // Best available damaging move (effectiveness 0 excluded when alternatives exist).
    const damagers = scored.filter(s => !s.isStatusMove && s.eff > 0);
    const bestDamager = damagers.length
      ? damagers.reduce((a, b) => (b.damageScore > a.damageScore ? b : a))
      : null;

    // Status play: if the player is status-free and we carry a usable ailment
    // move, lead with it — unless we already have a super-effective attack.
    const playerStatusFree = (this._player.status?.kind ?? 'none') === 'none';
    const ailmentMove = scored.find(s => Boolean(MOVE_STATUS[s.move.name]));
    if (
      playerStatusFree &&
      ailmentMove &&
      (!bestDamager || bestDamager.eff <= 1) &&
      Math.random() < 0.6
    ) {
      return ailmentMove.id;
    }

    if (bestDamager) return bestDamager.id;

    // No useful damaging option (all 0-effect or all status): fall back to any
    // usable move, preferring non-zero effectiveness when present.
    const fallback = scored.find(s => s.eff > 0) ?? scored[0];
    return fallback?.id ?? usable[0] ?? null;
  }

  private checkEnd(): boolean {
    if (this.ended) return true;
    if (this._opponent.hpCurrent <= 0 || this._opponent.fainted) {
      this.endBattle('victory');
      return true;
    }
    if (this._player.hpCurrent <= 0 || this._player.fainted) {
      this.endBattle('defeat');
      return true;
    }
    return false;
  }

  private endBattle(outcome: Outcome): void {
    if (this.ended) return;
    this.ended = true;
    const nextPhase: Phase = outcome;
    this._phase = nextPhase;
    this.emit({ kind: 'phase', phase: nextPhase });
    this.emit({ kind: 'end', outcome });
  }

  private setPhase(phase: Phase): void {
    if (this._phase === phase) return;
    this._phase = phase;
    this.emit({ kind: 'phase', phase });
  }

  private log(text: string): void {
    this.emit({ kind: 'log', text });
  }

  private emit(e: BattleEvent): void {
    this.onEvent(e);
  }

  private nameOf(side: Side): string {
    const p = side === 'player' ? this._player : this._opponent;
    return p.speciesId;
  }
}
